import { action, thunk } from 'easy-peasy'
import type { Action, Thunk } from 'easy-peasy'
import type { FMPStoreModel } from '../../types'
import type { EnrichCustomerResponse } from '@strata-monorepo/shared-types'
import type { BillEntryData } from '@strata-monorepo/shared-ui/components/BillEntry/BillEntry'
import type { CohesionGlobal } from '@strata-monorepo/shared-types/src/window'
import {
  trackUsagePrediction,
  isUsageTrackingFailure,
} from '@strata-monorepo/tracking/src/utils/trackUsagePrediction'
import consumptionPrediction from '../../utils/consumptionPrediction'
import { getIncense } from '../../error-handling/getIncense'

export interface ConsumptionPredictionModel {
  consumptionPredictionResponse: unknown

  setConsumptionPredictionResponse: Action<ConsumptionPredictionModel, unknown>

  fetchConsumptionPrediction: Thunk<
    ConsumptionPredictionModel,
    {
      cohesionData: CohesionGlobal
      enrichedData: EnrichCustomerResponse
      allEntries?: BillEntryData[]
    },
    null,
    FMPStoreModel,
    Promise<unknown>
  >
}

export const consumptionPredictionModel = (): ConsumptionPredictionModel => ({
  consumptionPredictionResponse: {},

  setConsumptionPredictionResponse: action((state, payload) => {
    state.consumptionPredictionResponse = payload
  }),

  fetchConsumptionPrediction: thunk(
    async (actions, request, { getStoreState }): Promise<unknown> => {
      try {
        const response = await consumptionPrediction(
          request.cohesionData,
          request.enrichedData,
          request.allEntries,
        )

        const { form } = getStoreState()
        const tracked = trackUsagePrediction(response, {
          formId: form.formId,
          // Read from the store rather than restated here: a questionnaire
          // config can override the version, and a literal would silently
          // diverge from every other event in the same session.
          formName: form.formVersion,
        })
        if (!tracked.emitted && isUsageTrackingFailure(tracked.reason)) {
          // Previously this path was silent, so a prediction that came back
          // without a usage value left no trace at all.
          getIncense()()
            .captureDetails({
              name: 'consumptionPredictionModel.fetchConsumptionPrediction.usageNotTracked',
              message: 'No usage tracking event emitted for the consumption prediction',
            })
            .setSafeData({ reason: tracked.reason })
            .sendError()
        }

        // Will not block the user from moving forward if no results are returned
        if (response?.success && response.data?.results) {
          actions.setConsumptionPredictionResponse(response)
        }
        return response
      } catch (error: unknown) {
        // Returning null still lets the quiz move on, but the request failing
        // is no longer invisible — without this, a model outage looked
        // identical to a model that simply had nothing to say.
        getIncense()(error)
          .captureDetails({
            name: 'consumptionPredictionModel.fetchConsumptionPrediction.requestFailed',
            message: 'Consumption prediction request failed',
          })
          .setSafeData({ errorMessage: error })
          .sendError()
        return null
      }
    },
  ),
})
